import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { F1065SchM3Service } from './f1065-sch-m3.service';
import { debounceTime, distinctUntilChanged, forkJoin, Subject, Subscription, switchMap } from 'rxjs';
import { parseAsLocalDate } from '../../../shared/_utils/date-utils';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { buildHeaderDataShell, generalConfigFromGlobal, normalizeFilterParams, rowPayloadFromActiveScreen, hydratedGlobalParams } from '../../../shared/workspace-element-context';

interface FormRow {
  num: string;
  description: string;
  value: any;
  value_user_input?: any;
  conditionalField?: boolean;
  tableCheckBox?: boolean;
  unallocatedAmt?: number;
  JSON_DATA?: any[];
  table_columns?: string[];
  saveParams?: string[];
  line_order?: string;
  lineAttrKey_a?: string;
  attrib_name_a?: string;
  attrib_order_a?: string;
  lineAttrKey_b?: string;
  attrib_name_b?: string;
  attrib_order_b?: string;
}

@Component({
    selector: 'gtw-f1065-sch-m3',
    templateUrl: './f1065-sch-m3.component.html',
    styleUrls: ['./f1065-sch-m3.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class F1065SchM3Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  private readonly subs = new Subscription();

  baseURLs: any;
  headerData: any;
  rowData: any;
  filterParams: any;
  globalParams: any;

  // Modal state
  minimise = false;
  showFormLoader = true;
  isSaving = false;

  // Form data
  title = '';
  modalTitle = 'Part I Financial Information and Net Income (Loss) Reconciliation';
  ISSUE_DESC = '';
  entityList: { [key: string]: any } = {};
  
  scheduleM3FormView: { rows: FormRow[] } = { rows: [] };
  originalData: { rows: FormRow[] } = { rows: [] };

  // Subtable data
  partnerData: any[] = [];
  dataLine: { [key: string]: any[] } = {
    line_4a: [],
    line_5a: [],
    line_5b: [],
    line_6a: [],
    line_6b: [],
    line_7a: [],
    line_7b: [],
    line_8: [],
    line_9: [],
    line_10: []
  };

  // Date picker state
  datePickerState: { startDate: boolean; endDate: boolean } = { startDate: false, endDate: false };

  // Business search
  businessDetails: any[] = [];
  searchSubject = new Subject<{ searchKey: string; line: string; rowIndex: number }>();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private service: F1065SchM3Service,
    private cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.setupSearchSubscription();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.initComponent();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.initComponent();
    }));
    this.initComponent();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private setupSearchSubscription(): void {
    this.subs.add(this.searchSubject.pipe(
      debounceTime(1000),
      distinctUntilChanged((prev, curr) => 
        prev.searchKey === curr.searchKey && prev.line === curr.line && prev.rowIndex === curr.rowIndex
      ),
      switchMap(({ searchKey, line, rowIndex }) => {
        const index = this.findRowIndex(line);
        if (index >= 0 && this.scheduleM3FormView.rows[index]?.JSON_DATA?.[rowIndex]) {
          this.scheduleM3FormView.rows[index].JSON_DATA![rowIndex].loading = true;
          const sourceType = (line === '5a' || line === '5b') ? 'F' : 'D';
          return this.service.searchBusinessInfo(
            this.getApiBaseUrl(),
            searchKey,
            sourceType,
            this.filterParams,
            this.globalParams
          );
        }
        return [];
      })
    ).subscribe({
      next: (response: any) => {
        if (response?.jsonObject?.length > 0) {
          this.businessDetails = response.jsonObject;
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.businessDetails = [];
        this.cdr.detectChanges();
      }
    }));
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.headerData = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
    this.filterParams = normalizeFilterParams(activeScreen) || {};
    this.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
  }

  private getApiBaseUrl(): string {
    return (
      this.baseURLs?.base_url ||
      this.baseURLs?.api ||
      this.baseURLs?.api?.baseURL ||
      this.baseURLs?.baseURL ||
      '/gtw'
    );
  }

  private initComponent(): void {
    if (!this.baseURLs || !this.rowData || !Object.keys(this.rowData).length) {
      this.showFormLoader = false;
      return;
    }
    const scenarioLabel = this.filterParams?.scenarioLabel || this.filterParams?.scenario_label || this.headerData?.scenarioLabel || 'Return As Filed';
    this.title = `${this.modalTitle} ( Tax Year : ${this.rowData.TAX_YEAR} - Scenario : ${scenarioLabel} )`;
    this.ISSUE_DESC = this.rowData.ISSUE_DESC || '';

    this.entityList = {
      'Tax Year': this.rowData.TAX_YEAR,
      'LEID': this.rowData.LEID,
      'LE Name': this.rowData.LE_NAME
    };

    // Debug logging for API parameters
    console.log('[F1065-Sch-M3] initComponent - globalParams:', this.globalParams);
    console.log('[F1065-Sch-M3] initComponent - filterParams:', this.filterParams);
    console.log('[F1065-Sch-M3] initComponent - rowData:', this.rowData);

    this.loadData();
  }

  private loadData(): void {
    this.showFormLoader = true;

    // First load subtable data, then form data
    // Pass globalParams to match AngularJS JsonObjectFactory.getJSONObj behavior
    this.service.loadSubTableData(
      this.getApiBaseUrl(),
      this.rowData,
      this.filterParams,
      this.globalParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (subTableResponse) => {
        console.log('[F1065-Sch-M3] Raw subtable API response:', subTableResponse);
        console.log('[F1065-Sch-M3] jsonObject from response:', subTableResponse?.jsonObject);
        this.processSubTableData(subTableResponse?.jsonObject || []);
        this.loadFormData();
      },
      error: (err) => {
        console.error('Error loading subtable data:', err);
        this.loadFormData();
      }
    });
  }

  private processSubTableData(data: any[]): void {
    console.log('[F1065-Sch-M3] processSubTableData received:', data);
    
    // Reset data
    this.partnerData = [];
    this.dataLine = {
      line_4a: [],
      line_5a: [],
      line_5b: [],
      line_6a: [],
      line_6b: [],
      line_7a: [],
      line_7b: [],
      line_8: [],
      line_9: [],
      line_10: []
    };

    data.forEach(row => {
      // Convert LINE_NO to string for comparison (API may return number or string)
      const lineNo = String(row.LINE_NO);
      console.log('[F1065-Sch-M3] Processing row with LINE_NO:', lineNo, 'raw:', row.LINE_NO);
      
      if (lineNo === 'D1') {
        row.DATA_ATTRIB_C = parseFloat(row.DATA_ATTRIB_C) || 0;
        this.partnerData.push(row);
      } else if (lineNo === '4a') {
        row.IncomeLossPerIncomeStmtAmt = parseFloat(row.IncomeLossPerIncomeStmtAmt) || 0;
        this.dataLine.line_4a.push(row);
      } else if (lineNo === '5a') {
        this.prepareSubRow(row, 'NetIncomeLossAmt');
        this.dataLine.line_5a.push(row);
      } else if (lineNo === '5b') {
        this.prepareSubRow(row, 'NetIncomeLossAmt');
        this.dataLine.line_5b.push(row);
      } else if (lineNo === '6a') {
        this.prepareSubRow(row, 'NetIncomeLossAmt');
        this.dataLine.line_6a.push(row);
      } else if (lineNo === '6b') {
        this.prepareSubRow(row, 'NetIncomeLossAmt');
        this.dataLine.line_6b.push(row);
      } else if (lineNo === '7a') {
        row.NetIncomeLossAmt = parseFloat(row.NetIncomeLossAmt) || 0;
        this.dataLine.line_7a.push(row);
      } else if (lineNo === '7b') {
        row.NetIncomeLossAmt = parseFloat(row.NetIncomeLossAmt) || 0;
        this.dataLine.line_7b.push(row);
      } else if (lineNo === '8') {
        row.Amt = parseFloat(row.Amt) || 0;
        this.dataLine.line_8.push(row);
      } else if (lineNo === '9') {
        row.Amt = parseFloat(row.Amt) || 0;
        this.dataLine.line_9.push(row);
      } else if (lineNo === '10') {
        row.Amt = parseFloat(row.Amt) || 0;
        this.dataLine.line_10.push(row);
      } else {
        console.log('[F1065-Sch-M3] Unknown LINE_NO:', lineNo);
      }
    });
    
    console.log('[F1065-Sch-M3] After processing - partnerData:', this.partnerData);
    console.log('[F1065-Sch-M3] After processing - dataLine:', this.dataLine);
  }

  private prepareSubRow(row: any, amtField: string): void {
    row.loading = false;
    row.showList = false;
    row.noRecords = false;
    // Concatenate BusinessNameLine1Txt and BusinessNameLine2Txt unconditionally (matching AngularJS behavior)
    row.BusinessNameLine1Txt = (row.BusinessNameLine1Txt || '') + (row.BusinessNameLine2Txt || '');
    row[amtField] = parseFloat(row[amtField]) || 0;
    row.EIN = row.EIN || row.MissingEINReasonCd || '';
  }

  private loadFormData(): void {
    this.service.loadFormData(
      this.getApiBaseUrl(),
      this.rowData,
      this.filterParams,
      this.globalParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.showFormLoader = false;
        const formItemList = response?.irsForm?.formItemList || [];
        this.transformFormData(formItemList);
        this.originalData = JSON.parse(JSON.stringify(this.scheduleM3FormView));
        this.computeValues2019();
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading form data:', err);
        this.showFormLoader = false;
        this._snackBar.open('Error loading form data', 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
        this.cdr.detectChanges();
      }
    });
  }

  private transformFormData(formItemList: any[]): void {
    console.log('[F1065-Sch-M3] transformFormData called with', formItemList.length, 'items');
    console.log('[F1065-Sch-M3] Current partnerData:', this.partnerData);
    console.log('[F1065-Sch-M3] Current dataLine:', this.dataLine);
    
    this.scheduleM3FormView.rows = [];

    for (let i = 0; i < formItemList.length; i++) {
      const dataRow = formItemList[i].dataRowList[0];
      const saveParams = dataRow.rowDimensions.object_id.split('~');
      const dataCellList = dataRow.dataCellList;
      const rowCount = dataCellList.length;

      const tempDataObj: FormRow = {
        num: dataCellList[0].name,
        description: dataCellList[1].name,
        value: null,
        saveParams: saveParams,
        line_order: saveParams[8]
      };

      if (rowCount > 2) {
        this.iterateArray(rowCount, dataCellList, tempDataObj);
      }

      this.scheduleM3FormView.rows.push(tempDataObj);
    }

    // Set initial values
    if (this.scheduleM3FormView.rows.length > 0) {
      this.scheduleM3FormView.rows[0].value = this.rowData.LE_NAME;
      this.scheduleM3FormView.rows[0].value_user_input = this.rowData.EIN;
    }
    
    // Log rows with JSON_DATA
    const rowsWithData = this.scheduleM3FormView.rows.filter(r => r.JSON_DATA && r.JSON_DATA.length > 0);
    console.log('[F1065-Sch-M3] Rows with JSON_DATA:', rowsWithData.map(r => ({ num: r.num, dataLength: r.JSON_DATA?.length, tableCheckBox: r.tableCheckBox })));
  }

  private iterateArray(count: number, array: any[], tempDataObj: FormRow): void {
    if (count === 3) {
      this.parseThreeColumns(array, tempDataObj);
    } else if (count > 4 && count < 9) {
      this.parseSubTableColumns(array, tempDataObj);
    } else {
      this.parseMultipleColumns(array, tempDataObj);
    }
  }

  private parseThreeColumns(array: any[], tempDataObj: FormRow): void {
    const keyParts = array[2].key.split('~');
    tempDataObj.lineAttrKey_a = keyParts[1];
    tempDataObj.attrib_name_a = keyParts[0];
    tempDataObj.attrib_order_a = keyParts[2];

    const value = array[2].value;
    tempDataObj.value = this.getCheckBoxValue(tempDataObj.num, value);

    if (tempDataObj.num === 'D') {
      tempDataObj.JSON_DATA = this.partnerData;
      console.log('[F1065-Sch-M3] Assigned partnerData to row D:', this.partnerData);
    }
  }

  private parseSubTableColumns(array: any[], tempDataObj: FormRow): void {
    const keyParts = array[2].key.split('~');
    tempDataObj.lineAttrKey_a = keyParts[1];
    tempDataObj.attrib_name_a = keyParts[0];
    tempDataObj.attrib_order_a = keyParts[2];
    tempDataObj.value = array[2].value ? parseFloat(array[2].value) : 0;

    const tableColumns: string[] = [];
    for (let key = 3; key < array.length; key++) {
      tableColumns.push(array[key].key.split('~')[0]);
    }
    tempDataObj.table_columns = tableColumns;

    // Assign JSON_DATA and calculate unallocated amount
    const lineDataMap: { [key: string]: { data: any[]; amtField: string } } = {
      '4a': { data: this.dataLine.line_4a, amtField: 'IncomeLossPerIncomeStmtAmt' },
      '5a': { data: this.dataLine.line_5a, amtField: 'NetIncomeLossAmt' },
      '5b': { data: this.dataLine.line_5b, amtField: 'NetIncomeLossAmt' },
      '6a': { data: this.dataLine.line_6a, amtField: 'NetIncomeLossAmt' },
      '6b': { data: this.dataLine.line_6b, amtField: 'NetIncomeLossAmt' },
      '7a': { data: this.dataLine.line_7a, amtField: 'NetIncomeLossAmt' },
      '7b': { data: this.dataLine.line_7b, amtField: 'NetIncomeLossAmt' },
      '8': { data: this.dataLine.line_8, amtField: 'Amt' },
      '9': { data: this.dataLine.line_9, amtField: 'Amt' },
      '10': { data: this.dataLine.line_10, amtField: 'Amt' }
    };

    const lineInfo = lineDataMap[tempDataObj.num];
    if (lineInfo) {
      tempDataObj.JSON_DATA = lineInfo.data;
      tempDataObj.tableCheckBox = lineInfo.data.length > 0;
      console.log('[F1065-Sch-M3] Assigned JSON_DATA to row', tempDataObj.num, ':', lineInfo.data, 'tableCheckBox:', tempDataObj.tableCheckBox);
      if (tempDataObj.tableCheckBox) {
        let total = 0;
        lineInfo.data.forEach(row => {
          const amt = row[lineInfo.amtField];
          if (!isNaN(amt)) {
            total += amt;
          }
        });
        tempDataObj.unallocatedAmt = (tempDataObj.value || 0) - total;
      } else {
        tempDataObj.unallocatedAmt = 0;
      }
    }
  }

  private parseMultipleColumns(array: any[], tempDataObj: FormRow): void {
    const keyPartsA = array[2].key.split('~');
    tempDataObj.lineAttrKey_a = keyPartsA[1];
    tempDataObj.attrib_name_a = keyPartsA[0];
    tempDataObj.attrib_order_a = keyPartsA[2];

    if (tempDataObj.num === 'B' || tempDataObj.num === 'C' || tempDataObj.num === '4b5') {
      tempDataObj.value = array[2].value;
      
      const keyPartsB = array[3].key.split('~');
      tempDataObj.lineAttrKey_b = keyPartsB[1];
      tempDataObj.attrib_name_b = keyPartsB[0];
      tempDataObj.attrib_order_b = keyPartsB[2];
      tempDataObj.value_user_input = array[3].value;

      if (tempDataObj.value === null || tempDataObj.value === 'false' || tempDataObj.value === '0') {
        tempDataObj.value = false;
        tempDataObj.conditionalField = true;
      } else if (tempDataObj.value === 'true') {
        tempDataObj.value = true;
        tempDataObj.conditionalField = false;
        if (tempDataObj.value_user_input == null || tempDataObj.value_user_input === '') {
          tempDataObj.value_user_input = 0;
        }
      } else {
        tempDataObj.value = false;
        tempDataObj.conditionalField = true;
      }
    } else if (['1a', '1b', '1c', '3a', '3b'].includes(tempDataObj.num)) {
      const keyPartsB = array[3].key.split('~');
      tempDataObj.lineAttrKey_b = keyPartsB[1];
      tempDataObj.attrib_name_b = keyPartsB[0];
      tempDataObj.attrib_order_b = keyPartsB[2];

      if (array[2].value === 'true') {
        tempDataObj.value = true;
      } else if (array[3].value === 'true') {
        tempDataObj.value = false;
      } else {
        tempDataObj.value = 'NA';
      }
    } else {
      tempDataObj.value = array[2].value;
      
      if (array.length > 3) {
        const keyPartsB = array[3].key.split('~');
        tempDataObj.lineAttrKey_b = keyPartsB[1];
        tempDataObj.attrib_name_b = keyPartsB[0];
        tempDataObj.attrib_order_b = keyPartsB[2];
        tempDataObj.value_user_input = array[3].value;
      }
    }
  }

  private getCheckBoxValue(rowNum: string, val: any): any {
    if (['A', 'D', 'E', '4b1', '4b2', '4b3', '4b4'].includes(rowNum)) {
      return val === 'true';
    } else if (rowNum === '2b' || rowNum === '2e') {
      // Use parseAsLocalDate to avoid timezone shift issues
      return parseAsLocalDate(val) || new Date();
    } else {
      return val ? parseFloat(val) : 0;
    }
  }

  private findRowIndex(lineNum: string): number {
    return this.scheduleM3FormView.rows.findIndex(r => r.num === lineNum);
  }

  // Field visibility toggle
  fieldVisibility(index: number, state: boolean, name?: string): void {
    const row = this.scheduleM3FormView.rows[index];
    if (!row) return;
    
    const rowNum = row.num;
    
    if (rowNum === 'B' || rowNum === 'C') {
      // B or C checkbox
      row.conditionalField = !state;
      if (!state) {
        row.value_user_input = '';
      } else if (row.value_user_input == null || row.value_user_input === '') {
        row.value_user_input = 0;
      }
    } else if (['4b1', '4b2', '4b3', '4b4', '4b5'].includes(rowNum)) {
      // 4b1-4b5 checkboxes - mutual exclusion logic
      const row4b5Index = this.findRowIndex('4b5');
      
      if (state && rowNum !== '4b5') {
        // Uncheck all other 4b checkboxes
        ['4b1', '4b2', '4b3', '4b4', '4b5'].forEach(num => {
          if (num !== rowNum) {
            const idx = this.findRowIndex(num);
            if (idx >= 0) {
              this.scheduleM3FormView.rows[idx].value = false;
            }
          }
        });
        // Disable 4b5 input
        if (row4b5Index >= 0) {
          this.scheduleM3FormView.rows[row4b5Index].conditionalField = true;
          this.scheduleM3FormView.rows[row4b5Index].value_user_input = '';
        }
      } else if (state && rowNum === '4b5') {
        // Uncheck 4b1-4b4
        ['4b1', '4b2', '4b3', '4b4'].forEach(num => {
          const idx = this.findRowIndex(num);
          if (idx >= 0) {
            this.scheduleM3FormView.rows[idx].value = false;
          }
        });
        // Enable 4b5 input
        if (row4b5Index >= 0) {
          this.scheduleM3FormView.rows[row4b5Index].conditionalField = false;
        }
      } else {
        // Disable 4b5 input and clear text when unchecking
        if (row4b5Index >= 0) {
          this.scheduleM3FormView.rows[row4b5Index].conditionalField = true;
          this.scheduleM3FormView.rows[row4b5Index].value_user_input = '';
        }
      }
    } else {
      row.conditionalField = !row.conditionalField;
      if ((name === 'yes' && !state) || (name === 'no' && state)) {
        row.value = 'NA';
      }
    }
  }

  // Add net income/loss amount
  addNetIncomeLossAmt(line: string): void {
    const validLines = ['4a', '5a', '5b', '6a', '6b', '7a', '7b', '8', '9', '10'];
    if (!validLines.includes(line)) return;

    const index = this.findRowIndex(line);
    if (index < 0) return;

    let totalCount = 0;
    const amtCol = line === '4a' ? 'IncomeLossPerIncomeStmtAmt'
      : (['8', '9', '10'].includes(line) ? 'Amt' : 'NetIncomeLossAmt');
    
    this.scheduleM3FormView.rows[index].JSON_DATA?.forEach(row => {
      const val = parseFloat(row[amtCol]);
      if (!isNaN(val)) {
        totalCount += this.roundToThree(val);
      }
    });

    this.scheduleM3FormView.rows[index].unallocatedAmt = 
      (parseFloat(this.scheduleM3FormView.rows[index].value) || 0) - totalCount;
  }

  // Limit decimal to max 100
  limitDecimal(val: number, line: string, i: number): void {
    const index = this.findRowIndex(line);
    if (index >= 0 && this.scheduleM3FormView.rows[index].JSON_DATA) {
      if (Number(val) > 100) {
        this.scheduleM3FormView.rows[index].JSON_DATA![i].DATA_ATTRIB_C = 100;
      }
    }
  }

  // Add rows to subtables
  addRows(type: string, i: number, arr?: string[]): void {
    let newRow: any = {};
    
    if (type === 'dynamic' && arr) {
      arr.forEach(col => {
        newRow[col] = '';
      });
      newRow.loading = false;
      newRow.showList = false;
      newRow.noRecords = false;
      newRow.OCCURRENCE = (this.scheduleM3FormView.rows[i].JSON_DATA?.length || 0) + 1;
    } else {
      newRow = {
        OCCURRENCE: (this.scheduleM3FormView.rows[i].JSON_DATA?.length || 0) + 1,
        DATA_ATTRIB_A: '',
        DATA_ATTRIB_B: '',
        DATA_ATTRIB_C: 0
      };
    }
    
    if (!this.scheduleM3FormView.rows[i].JSON_DATA) {
      this.scheduleM3FormView.rows[i].JSON_DATA = [];
    }
    this.scheduleM3FormView.rows[i].JSON_DATA!.push(newRow);
  }

  // Remove row from subtable
  removeRow(rowIndex: number, subRowIndex: number, line: string): void {
    if (this.scheduleM3FormView.rows[rowIndex].JSON_DATA) {
      this.scheduleM3FormView.rows[rowIndex].JSON_DATA!.splice(subRowIndex, 1);
      this.addNetIncomeLossAmt(line);
    }
  }

  // Compute line 11 value (sum of lines 4a, 5a, 5b, 6a, 6b, 7a, 7b, 8, 9, 10)
  computeValues2019(): void {
    let sum = 0;
    // Sum rows: 4a, 5a, 5b, 6a, 6b, 7a, 7b, 8, 9, 10
    const lineNums = ['4a', '5a', '5b', '6a', '6b', '7a', '7b', '8', '9', '10'];
    
    lineNums.forEach(lineNum => {
      const idx = this.findRowIndex(lineNum);
      if (idx >= 0 && this.scheduleM3FormView.rows[idx]) {
        const val = parseFloat(this.scheduleM3FormView.rows[idx].value);
        if (!isNaN(val)) {
          sum += val;
        }
      }
    });
    
    // Set line 11 value
    const line11Idx = this.findRowIndex('11');
    if (line11Idx >= 0 && this.scheduleM3FormView.rows[line11Idx]) {
      this.scheduleM3FormView.rows[line11Idx].value = sum;
    }
  }

  // Business search
  getBusinessInfo(searchKey: string, line: string, rowIndex: number): void {
    if (!searchKey) return;
    
    const index = this.findRowIndex(line);
    if (index >= 0 && this.scheduleM3FormView.rows[index].JSON_DATA?.[rowIndex]) {
      this.scheduleM3FormView.rows[index].JSON_DATA![rowIndex].loading = true;
      this.scheduleM3FormView.rows[index].JSON_DATA![rowIndex].noRecords = false;
      
      this.searchSubject.next({ searchKey, line, rowIndex });
    }
  }

  // Fill textbox from search result
  fillTextbox(searchedData: any, line: string, rowIndex: number): void {
    const index = this.findRowIndex(line);
    if (index >= 0 && this.scheduleM3FormView.rows[index].JSON_DATA?.[rowIndex]) {
      this.scheduleM3FormView.rows[index].JSON_DATA![rowIndex].BusinessNameLine1Txt = searchedData.LE_NAME;
      this.scheduleM3FormView.rows[index].JSON_DATA![rowIndex].EIN = searchedData.EIN;
      this.scheduleM3FormView.rows[index].JSON_DATA![rowIndex].showList = false;
      this.scheduleM3FormView.rows[index].JSON_DATA![rowIndex].loading = false;
    }
  }

  // Date change handler
  onDateChange(index: number, event: any): void {
    if (event) {
      // Use parseAsLocalDate to avoid timezone shift issues
      this.scheduleM3FormView.rows[index].value = parseAsLocalDate(event);
    }
  }

  private roundToThree(num: number): number {
    return +(Math.round(Number(num + 'e+3')) + 'e-3');
  }

  // Format date for display
  formatDate(date: Date | null): string {
    if (!date) return '';
    const d = new Date(date);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  save(): void {
    // Validate dates (row 2b = start date, row 2e = end date)
    const startDateIdx = this.findRowIndex('2b');
    const endDateIdx = this.findRowIndex('2e');
    if (startDateIdx >= 0 && endDateIdx >= 0) {
      const startDate = this.scheduleM3FormView.rows[startDateIdx]?.value;
      const endDate = this.scheduleM3FormView.rows[endDateIdx]?.value;
      if (startDate && endDate && startDate > endDate) {
        this._snackBar.open('End date cannot be before Start date', 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
        return;
      }
    }

    this.isSaving = true;
    const formData = this.prepareSaveJson();
    const processName = this.headerData?.screenLabel || '1065 Textual Information';

    this.service.saveScheduleM3Form(
      this.getApiBaseUrl(),
      this.rowData,
      formData,
      this.filterParams,
      processName,
      this.globalParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.isSaving = false;
        if (response.callSuccess === '1' && response.errorMessage === 'null') {
          this._snackBar.open('Transactions saved successfully', 'Close', { duration: 4000, panelClass: ['success-snackbar'] });
          // Match f1065-sch-c contract: emit string 'refresh' so CeHostDialog
          // closes the dialog with `{ action: 'refresh', detail: 'refresh' }`
          // and the grid reloads exactly once. The previous implementation
          // emitted an object payload and then chained `cancel()`, which —
          // depending on whether the dialog had already closed — could fire
          // duplicate refreshes or none at all.
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.errorMessage || 'Error saving data', 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isSaving = false;
        console.error('Save error:', err);
        this._snackBar.open('Error saving data', 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
        this.cdr.detectChanges();
      }
    });
  }

  private prepareSaveJson(): any[] {
    const tempArray: any[] = [];
    
    // Format dates before save (row 2b = start date, row 2e = end date)
    const startDateIdx = this.findRowIndex('2b');
    const endDateIdx = this.findRowIndex('2e');
    if (startDateIdx >= 0 && this.scheduleM3FormView.rows[startDateIdx]) {
      this.scheduleM3FormView.rows[startDateIdx].value = this.formatDate(this.scheduleM3FormView.rows[startDateIdx].value);
    }
    if (endDateIdx >= 0 && this.scheduleM3FormView.rows[endDateIdx]) {
      this.scheduleM3FormView.rows[endDateIdx].value = this.formatDate(this.scheduleM3FormView.rows[endDateIdx].value);
    }

    for (let i = 0; i < this.scheduleM3FormView.rows.length; i++) {
      const row = this.scheduleM3FormView.rows[i];
      
      const formRowDataA: any = {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: row.saveParams?.[1] || '',
        line_order: row.line_order,
        attrib_order: row.attrib_order_a,
        line_description: row.description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: row.saveParams?.[2] || '',
        line_no: row.num,
        occurence: row.saveParams?.[7] || '',
        line_attrib_key: row.lineAttrKey_a,
        Attribute_Name: row.attrib_name_a,
        Attribute_Value: this.getAttributeValue(row.value),
        trans_dtls_key: row.saveParams?.[3] || '',
        trans_status: row.saveParams?.[6] || '',
        is_dirty: '',
        JSON_DATA: []
      };

      // Handle subtable data
      if (this.shouldIncludeJsonData(row, i)) {
        formRowDataA.JSON_DATA = this.buildJsonData(row, i);
      }

      tempArray.push(formRowDataA);

      // Handle secondary attribute
      if (row.attrib_name_b) {
        const formRowDataB: any = {
          tax_year: this.rowData.TAX_YEAR,
          trans_type_key: row.saveParams?.[1] || '',
          line_order: row.line_order,
          attrib_order: row.attrib_order_b,
          line_description: row.description,
          combination_key: this.rowData.COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: row.saveParams?.[2] || '',
          line_no: row.num,
          occurence: row.saveParams?.[7] || '',
          line_attrib_key: row.lineAttrKey_b,
          Attribute_Name: row.attrib_name_b,
          Attribute_Value: this.getSecondaryAttributeValue(row),
          trans_dtls_key: row.saveParams?.[3] || '',
          trans_status: row.saveParams?.[6] || '',
          is_dirty: ''
        };

        if (row.num !== 'D1') {
          tempArray.push(formRowDataB);
        }
      }
    }

    return [{
      addrows: [],
      editrows: [tempArray],
      removerows: []
    }];
  }

  private getAttributeValue(value: any): string {
    if (value === true) return 'true';
    if (value === false || value === 'NA') return 'false';
    return value?.toString() || '';
  }

  private getSecondaryAttributeValue(row: FormRow): string {
    const specialLines = ['PSHIP', 'B', 'C', '4b5', '12a', '12b', '12c', '12d'];
    if (specialLines.includes(row.num)) {
      return row.value_user_input || '';
    }
    // For yes/no radio buttons, invert the value
    if (row.value === true) return 'false';
    if (row.value === false || row.value === 'NA') return 'true';
    return row.value?.toString() || '';
  }

  private shouldIncludeJsonData(row: FormRow, index: number): boolean {
    const subTableLines = ['4a', '5a', '5b', '6a', '6b', '7a', '7b', '8', '9', '10'];
    if (subTableLines.includes(row.num)) return true;
    
    // D1 includes partner data if D checkbox is true
    if (row.num === 'D1') {
      // Find row D by num instead of using index-1
      const rowDIdx = this.findRowIndex('D');
      if (rowDIdx >= 0) {
        const rowD = this.scheduleM3FormView.rows[rowDIdx];
        return rowD?.value === true && (rowD?.JSON_DATA?.length || 0) > 0;
      }
    }
    
    return false;
  }

  private buildJsonData(row: FormRow, index: number): any[] {
    const jsonData: any[] = [];
    let attributes: string[] = [];
    let dataArray: any[] = [];

    if (row.num === 'D1') {
      attributes = ['A', 'B', 'C'];
      // Find row D by num instead of using index-1
      const rowDIdx = this.findRowIndex('D');
      if (rowDIdx >= 0) {
        dataArray = this.scheduleM3FormView.rows[rowDIdx].JSON_DATA || [];
      }
    } else {
      attributes = row.table_columns || [];
      dataArray = row.JSON_DATA || [];
    }

    dataArray.forEach((dataRow, rowKey) => {
      let hasData = false;
      const tempDataArray: any[] = [];

      attributes.forEach((attr, colKey) => {
        let tempObj: any;
        
        if (row.num === 'D1') {
          const occurrence = Math.floor(jsonData.length / 3) + 1;
          tempObj = {
            OCCURRENCE: occurrence,
            ATTRIB_NAME: 'DATA_ATTRIB_' + attr,
            ATTRIB_VALUE: dataRow['DATA_ATTRIB_' + attr]
          };
        } else {
          const occurrence = Math.floor(jsonData.length / attributes.length) + 1;
          let attrValue = dataRow[attr];
          
          // Special handling for BusinessNameLine1Txt
          if (attr === 'BusinessNameLine1Txt') {
            attrValue = (dataRow['BusinessNameLine1Txt'] || '').slice(0, 76);
          } else if (attr === 'BusinessNameLine2Txt') {
            attrValue = (dataRow['BusinessNameLine1Txt'] || '').slice(76);
          }
          
          tempObj = {
            OCCURRENCE: occurrence,
            ATTRIB_NAME: attr,
            ATTRIB_VALUE: attrValue
          };
        }

        if (tempObj.ATTRIB_VALUE !== undefined && tempObj.ATTRIB_VALUE !== '' && tempObj.ATTRIB_VALUE !== null) {
          hasData = true;
        }
        tempDataArray.push(tempObj);
      });

      if (hasData) {
        tempDataArray.forEach(col => jsonData.push(col));
      }
    });

    return jsonData;
  }

  reset(): void {
    this.scheduleM3FormView = JSON.parse(JSON.stringify(this.originalData));
    this.computeValues2019();
    this.cdr.detectChanges();
  }

  cancel(): void {
    // Match the contract of CeHostDialog (and the other partnership form
    // components, e.g. f1065-sch-c): emit the literal string 'cancel'.
    // CeHostDialog branches on `detail === 'cancel'` to call
    // `dialogRef.close({ action: 'close' })` (no grid refresh). Anything
    // else falls through to `{ action: 'refresh', detail }`, which is what
    // used to make the underlying grid reload on every X click.
    this.requestGridUpdate.emit('cancel');
  }

  toggleMinimize(): void {
    // Pure internal UI toggle — modal-body / modal-footer have
    // `[hidden]="minimise"`. Nothing should be emitted to the host dialog
    // here (the previous implementation emitted a `request-grid-update`
    // event, which CeHostDialog interpreted as a refresh-and-close).
    this.minimise = !this.minimise;
  }

  // Helper for template
  objectKeys(obj: any): string[] {
    return Object.keys(obj || {});
  }
}

